import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { isCanceledError } from 'src/helper/api';
import type {
  ChatState,
  Conversation,
  ConversationResponse,
  ConversationsResponse,
  CreateConversationResponse,
  Message,
  SendMessageResponse,
} from './types';

interface ApiResponse<T> {
  data: T;
  message?: string;
}

const markThreadListFailure = (state: ChatState, recordId: string) => {
  state.threadListLoadFailedFor = { ...state.threadListLoadFailedFor, [recordId]: true };
};

const clearThreadListFailure = (state: ChatState, recordId?: string) => {
  if (recordId && state.threadListLoadFailedFor?.[recordId]) {
    state.threadListLoadFailedFor[recordId] = false;
  }
};

const deriveConversationFlags = (conv: Conversation): Conversation => {
  const conversationType = conv.conversation_type?.toLowerCase()?.trim();

  const derivedFlags = {
    is_vendor_conversation: conversationType === 'vendor',
    is_technician_conversation: conversationType === 'technician',
    is_resident_conversation: conversationType === 'resident',
    is_coordinator_conversation: conversationType === 'coordinator',
  };

  const toBoolean = (value: any): boolean => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
      if (normalized === '1') return true;
      if (normalized === '0') return false;
      return Boolean(normalized);
    }

    if (value === 1 || value === 0) return Boolean(value);
    return Boolean(value);
  };

  // Technician conversations should also be treated as vendor conversations for display purposes
  const isTechnician =
    toBoolean(conv.is_technician_conversation) || derivedFlags.is_technician_conversation;
  const isVendor = toBoolean(conv.is_vendor_conversation) || derivedFlags.is_vendor_conversation;

  return {
    ...conv,
    is_vendor_conversation: isVendor || isTechnician,
    is_technician_conversation: isTechnician,
    is_resident_conversation:
      toBoolean(conv.is_resident_conversation) || derivedFlags.is_resident_conversation,
    is_coordinator_conversation:
      toBoolean(conv.is_coordinator_conversation) || derivedFlags.is_coordinator_conversation,
  };
};

const extraReducers = (builder: ActionReducerMapBuilder<ChatState>) => {
  builder
    // Send Message
    .addMatcher(
      (action): action is { type: string } => action.type === 'chat/sendMessage/pending',
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    )
    .addMatcher(
      (
        action,
      ): action is { type: string; payload: SendMessageResponse & { conversationId?: string } } =>
        action.type === 'chat/sendMessage/fulfilled',
      (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          const newMessage = action.payload.data;

          // The send response (MessageResource) names its conversation `conversation_uuid`
          // and carries no `conversation_id` (COM-687); websocket-sourced payloads instead
          // carry the same uuid under `conversation_id`. As a final fallback use the thunk's
          // conversationId (merged into the payload by api.ts, mirroring getWorkOrderConversation)
          // so the sender's own message always lands in Redux instead of being silently
          // skipped. Without this it lived only as the panel's local optimistic row, which no
          // reducer can reach — so delivered/read receipts had nothing to advance and the
          // sender's bubble sat on a single checkmark until a refetch. (ORI-645 / COM-687)
          const conversationId =
            newMessage.conversation_uuid ||
            newMessage.conversation_id ||
            action.payload.conversationId;

          // Can't place a message we can't tie to a conversation. (ORI-645)
          if (!conversationId) return;

          // Normalize the message ID, and store the conversation under the key the rest of
          // the slice reads so a stored row looks the same however it arrived. (COM-687)
          const normalizedMessage = {
            ...newMessage,
            id: newMessage.uuid || newMessage.id,
            conversation_id: conversationId,
          };

          // This push newly reaches the sender's own message (the conversationId fallback
          // above), so it must dedupe like every other message-write in this slice —
          // otherwise the sender's own Pusher echo (addIncomingMessage) plus this push
          // double-insert the same uuid. (ORI-645, HIGH-2)
          const alreadyPresent = (msgs?: Array<{ uuid?: string; id?: string }>) =>
            (msgs ?? []).some(
              (m) =>
                (!!normalizedMessage.uuid && m.uuid === normalizedMessage.uuid) ||
                (!!normalizedMessage.id && m.id === normalizedMessage.id),
            );

          // Add to messages array (legacy support)
          state.messages.push(action.payload.data as any);

          // Update current conversation if it matches
          if (
            state.currentConversation &&
            (state.currentConversation.id === conversationId ||
              state.currentConversation.uuid === conversationId)
          ) {
            if (!state.currentConversation.messages) {
              state.currentConversation.messages = [];
            }
            if (!alreadyPresent(state.currentConversation.messages)) {
              state.currentConversation.messages.push(normalizedMessage);
              state.currentConversation.messages_count = state.currentConversation.messages.length;
              state.currentConversation.latest_message = normalizedMessage;
            }
          }

          // Update conversation in conversations array
          const conversationIndex = state.conversations.findIndex(
            (conv) => conv.id === conversationId || conv.uuid === conversationId,
          );
          if (conversationIndex !== -1) {
            const conversation = state.conversations[conversationIndex];
            if (!conversation.messages) {
              conversation.messages = [];
            }
            if (!alreadyPresent(conversation.messages)) {
              conversation.messages.push(normalizedMessage);
              conversation.messages_count = conversation.messages.length;
              conversation.latest_message = normalizedMessage;
            }
          }

          // Helper function to update message in cache
          const updateCacheConversation = (
            cache: { conversations: any[]; lastFetched: number } | undefined,
            targetConversationId: string,
          ) => {
            if (!cache) return;
            const cacheConv = cache.conversations.find(
              (conv) => conv.id === targetConversationId || conv.uuid === targetConversationId,
            );
            if (cacheConv) {
              if (!cacheConv.messages) {
                cacheConv.messages = [];
              }
              if (!alreadyPresent(cacheConv.messages)) {
                cacheConv.messages.push(normalizedMessage);
                cacheConv.messages_count = cacheConv.messages.length;
                cacheConv.latest_message = normalizedMessage;
              }
            }
          };

          // Update in serviceRequestConversationsCache
          Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
            updateCacheConversation(cache, conversationId);
          });

          // Update in workOrderConversationsCache
          Object.values(state.workOrderConversationsCache).forEach((cache) => {
            updateCacheConversation(cache, conversationId);
          });
        }
      },
    )
    .addMatcher(
      (action): action is { type: string; error: { message?: string } } =>
        action.type === 'chat/sendMessage/rejected',
      (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to send message';
      },
    )

    // Upload Attachments
    .addMatcher(
      (action): action is { type: string } =>
        action.type === 'chat/uploadConversationAttachments/pending',
      (state) => {
        state.uploadingFiles = true;
        state.error = null;
      },
    )
    .addMatcher(
      (action): action is { type: string } =>
        action.type === 'chat/uploadConversationAttachments/fulfilled',
      (state) => {
        state.uploadingFiles = false;
      },
    )
    .addMatcher(
      (action): action is { type: string; error: { message?: string } } =>
        action.type === 'chat/uploadConversationAttachments/rejected',
      (state, action) => {
        state.uploadingFiles = false;
        state.error = action.error.message || 'Failed to upload attachments';
      },
    )

    // Get Messages
    .addMatcher(
      (action): action is { type: string } =>
        action.type === 'chat/getConversationMessages/pending',
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    )
    .addMatcher(
      (action): action is { type: string; payload: ApiResponse<Message[]> } =>
        action.type === 'chat/getConversationMessages/fulfilled',
      (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          state.messages = action.payload.data;
        }
      },
    )
    .addMatcher(
      (action): action is { type: string; error: { message?: string } } =>
        action.type === 'chat/getConversationMessages/rejected',
      (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load messages';
      },
    )

    // Get Work Order Conversation
    .addMatcher(
      (action): action is { type: string } =>
        action.type === 'chat/getWorkOrderConversation/pending',
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    )
    .addMatcher(
      (
        action,
      ): action is { type: string; payload: ConversationsResponse & { workOrderId?: string } } =>
        action.type === 'chat/getWorkOrderConversation/fulfilled',
      (state, action) => {
        state.isLoading = false;
        const workOrderId = action.payload?.workOrderId;
        clearThreadListFailure(state, workOrderId);

        const normalizedConversations = (action.payload?.data || []).map((conv) => {
          const normalizedMessages =
            conv.messages?.map((msg) => ({
              ...msg,
              id: msg.uuid || msg.id,
            })) || [];

          const normalizedParticipants =
            conv.participants?.map((participant) => ({
              ...participant,
              id: participant.uuid || participant.id,
            })) || [];

          const convWithFlags = deriveConversationFlags(conv);

          return {
            ...convWithFlags,
            id: convWithFlags.uuid || convWithFlags.id,
            messages: normalizedMessages,
            participants: normalizedParticipants,
          };
        });

        if (workOrderId) {
          state.workOrderConversationsCache[workOrderId] = {
            conversations: normalizedConversations,
            lastFetched: Date.now(),
          };
        }

        // Resident conversations are SR-scoped (work_order_uuid = null) so the WO endpoint
        // returns them via a service_request_uuid join. Keep the SR cache in sync so the
        // Service Request chat panel always reflects the latest state without a separate fetch.
        normalizedConversations.forEach((normalizedConv) => {
          if (!normalizedConv.is_resident_conversation) return;
          const srId = normalizedConv.service_request_uuid || normalizedConv.service_request_id;
          if (!srId) return;
          if (!state.serviceRequestConversationsCache[srId]) {
            state.serviceRequestConversationsCache[srId] = {
              conversations: [],
              lastFetched: Date.now(),
            };
          }
          const srCache = state.serviceRequestConversationsCache[srId];
          const idx = srCache.conversations.findIndex(
            (c) => c.id === normalizedConv.id || c.uuid === normalizedConv.id,
          );
          if (idx !== -1) {
            srCache.conversations[idx] = normalizedConv;
          } else {
            srCache.conversations.push(normalizedConv);
          }
          srCache.lastFetched = Date.now();
        });

        normalizedConversations.forEach((normalizedConv) => {
          const conversationId = normalizedConv.id;
          const index = state.conversations.findIndex(
            (conv) => conv.id === conversationId || conv.uuid === conversationId,
          );
          if (index !== -1) {
            state.conversations[index] = normalizedConv;
          } else {
            state.conversations.push(normalizedConv);
          }
        });

        if (normalizedConversations.length > 0) {
          state.currentConversation = normalizedConversations[0];
        }
      },
    )
    .addMatcher(
      (
        action,
      ): action is {
        type: string;
        error: { message?: string };
        payload?: { workOrderId?: string };
      } => action.type === 'chat/getWorkOrderConversation/rejected',
      (state, action) => {
        // A canceled read says nothing about the record: a newer one is on its way.
        if (action.payload?.workOrderId && !isCanceledError(action.payload)) {
          markThreadListFailure(state, action.payload.workOrderId);
        }
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load conversation';
      },
    )

    // Get Service Request Conversation
    .addMatcher(
      (action): action is { type: string } =>
        action.type === 'chat/getServiceRequestConversation/pending',
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    )
    .addMatcher(
      (
        action,
      ): action is {
        type: string;
        payload: ConversationsResponse & { serviceRequestId?: string; alternateKey?: string };
      } => action.type === 'chat/getServiceRequestConversation/fulfilled',
      (state, action) => {
        state.isLoading = false;
        const serviceRequestId = action.payload?.serviceRequestId;
        // The number fallback answers for the UUID it mirrors onto (`alternateKey`).
        const threadListKey = action.payload?.alternateKey ?? serviceRequestId;
        clearThreadListFailure(state, threadListKey);

        const normalizedConversations = (action.payload?.data || []).map((conv) => {
          const normalizedMessages =
            conv.messages?.map((msg) => ({
              ...msg,
              id: msg.uuid || msg.id,
            })) || [];

          const normalizedParticipants =
            conv.participants?.map((participant) => ({
              ...participant,
              id: participant.uuid || participant.id,
            })) || [];

          const convWithFlags = deriveConversationFlags(conv);

          return {
            ...convWithFlags,
            id: convWithFlags.uuid || convWithFlags.id,
            messages: normalizedMessages,
            participants: normalizedParticipants,
          };
        });

        if (serviceRequestId) {
          state.serviceRequestConversationsCache[serviceRequestId] = {
            conversations: normalizedConversations,
            lastFetched: Date.now(),
          };
        }

        if (normalizedConversations.length > 0) {
          const srUuid = normalizedConversations[0]?.service_request_uuid;
          if (srUuid && srUuid !== serviceRequestId) {
            state.serviceRequestConversationsCache[srUuid] = {
              conversations: normalizedConversations,
              lastFetched: Date.now(),
            };
          }
        }

        const alternateKey = action.payload?.alternateKey;
        if (alternateKey && alternateKey !== serviceRequestId) {
          state.serviceRequestConversationsCache[alternateKey] = {
            conversations: normalizedConversations,
            lastFetched: Date.now(),
          };
        }

        normalizedConversations.forEach((normalizedConv) => {
          const conversationId = normalizedConv.id;
          const index = state.conversations.findIndex(
            (conv) => conv.id === conversationId || conv.uuid === conversationId,
          );
          if (index !== -1) {
            state.conversations[index] = normalizedConv;
          } else {
            state.conversations.push(normalizedConv);
          }
        });
      },
    )
    .addMatcher(
      (
        action,
      ): action is {
        type: string;
        error: { message?: string };
        payload?: { serviceRequestId?: string; alternateKey?: string };
      } => action.type === 'chat/getServiceRequestConversation/rejected',
      (state, action) => {
        const threadListKey = action.payload?.alternateKey ?? action.payload?.serviceRequestId;
        // A canceled read says nothing about the record: a newer one is on its way.
        if (threadListKey && !isCanceledError(action.payload)) {
          markThreadListFailure(state, threadListKey);
        }
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load conversations';
      },
    )

    // Get Conversation By ID
    .addMatcher(
      (action): action is { type: string } => action.type === 'chat/getConversationById/pending',
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    )
    .addMatcher(
      (action): action is { type: string; payload: ConversationResponse } =>
        action.type === 'chat/getConversationById/fulfilled',
      (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          const conversation = action.payload.data;

          const normalizedMessages =
            conversation.messages?.map((msg) => ({
              ...msg,
              id: msg.uuid || msg.id,
            })) || [];

          const normalizedParticipants =
            conversation.participants?.map((participant) => ({
              ...participant,
              id: participant.uuid || participant.id,
            })) || [];

          const convWithFlags = deriveConversationFlags(conversation);

          const paginationMeta = action.payload.meta?.messages_pagination;
          const paginationLinks = action.payload.links;

          const normalizedConversation = {
            ...convWithFlags,
            id: convWithFlags.uuid || convWithFlags.id,
            messages: normalizedMessages,
            participants: normalizedParticipants,
            messages_next_url: paginationLinks?.next || null,
            messages_has_more: paginationMeta?.has_more_pages || false,
          };

          state.currentConversation = normalizedConversation;

          const conversationId = normalizedConversation.id;
          const index = state.conversations.findIndex(
            (conv) => conv.id === conversationId || conv.uuid === conversationId,
          );
          if (index !== -1) {
            state.conversations[index] = normalizedConversation;
          } else {
            state.conversations.push(normalizedConversation);
          }

          const serviceRequestId =
            conversation.service_request_uuid || conversation.service_request_id;
          if (serviceRequestId && state.serviceRequestConversationsCache[serviceRequestId]) {
            const cacheIndex = state.serviceRequestConversationsCache[
              serviceRequestId
            ].conversations.findIndex(
              (conv) => conv.id === conversationId || conv.uuid === conversationId,
            );
            if (cacheIndex !== -1) {
              state.serviceRequestConversationsCache[serviceRequestId].conversations[cacheIndex] =
                normalizedConversation;
            }
          }

          const workOrderId = conversation.work_order_uuid || conversation.work_order_id;
          if (workOrderId && state.workOrderConversationsCache[workOrderId]) {
            const cacheIndex = state.workOrderConversationsCache[
              workOrderId
            ].conversations.findIndex(
              (conv) => conv.id === conversationId || conv.uuid === conversationId,
            );
            if (cacheIndex !== -1) {
              state.workOrderConversationsCache[workOrderId].conversations[cacheIndex] =
                normalizedConversation;
            }
          }
        }
      },
    )
    .addMatcher(
      (action): action is { type: string; error: { message?: string } } =>
        action.type === 'chat/getConversationById/rejected',
      (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load conversation';
      },
    )

    .addMatcher(
      (action): action is { type: string } => action.type === 'chat/createConversation/pending',
      (state) => {
        state.isCreatingConversation = true;
        state.error = null;
      },
    )
    .addMatcher(
      (action): action is { type: string; payload: CreateConversationResponse } =>
        action.type === 'chat/createConversation/fulfilled',
      (state, action) => {
        state.isCreatingConversation = false;
        if (action.payload?.data) {
          const conversation = action.payload.data;

          const normalizedMessages =
            conversation.messages?.map((msg) => ({
              ...msg,
              id: msg.uuid || msg.id,
            })) || [];

          const normalizedParticipants =
            conversation.participants?.map((participant) => ({
              ...participant,
              id: participant.uuid || participant.id,
            })) || [];

          const convWithFlags = deriveConversationFlags(conversation);

          const normalizedConversation = {
            ...convWithFlags,
            id: convWithFlags.uuid || convWithFlags.id,
            messages: normalizedMessages,
            participants: normalizedParticipants,
          };

          state.currentConversation = normalizedConversation;
          state.conversations.push(normalizedConversation);

          const serviceRequestId =
            conversation.service_request_uuid || conversation.service_request_id;
          if (serviceRequestId) {
            if (!state.serviceRequestConversationsCache[serviceRequestId]) {
              state.serviceRequestConversationsCache[serviceRequestId] = {
                conversations: [],
                lastFetched: Date.now(),
              };
            }
            state.serviceRequestConversationsCache[serviceRequestId].conversations.push(
              normalizedConversation,
            );
            state.serviceRequestConversationsCache[serviceRequestId].lastFetched = Date.now();
          }

          // Add to work order cache if applicable.
          // NOTE: Resident conversations are created with work_order_uuid = null (SR-scoped),
          // so they will NOT be added to workOrderConversationsCache here.
          // The component that creates the conversation must re-fetch getWorkOrderConversation
          // to refresh the WO cache and surface the new resident thread in the WO chat panel.
          // TODO: Remove this re-fetch once the FE subscribes to the service-request-scoped
          // real-time channel (HandleServiceRequestMessageNotifications).
          const workOrderId = conversation.work_order_uuid || conversation.work_order_id;
          if (workOrderId) {
            if (!state.workOrderConversationsCache[workOrderId]) {
              state.workOrderConversationsCache[workOrderId] = {
                conversations: [],
                lastFetched: Date.now(),
              };
            }
            state.workOrderConversationsCache[workOrderId].conversations.push(
              normalizedConversation,
            );
            state.workOrderConversationsCache[workOrderId].lastFetched = Date.now();
          }
        }
      },
    )
    .addMatcher(
      (action): action is { type: string; error: { message?: string } } =>
        action.type === 'chat/createConversation/rejected',
      (state, action) => {
        state.isCreatingConversation = false;
        state.error = action.error.message || 'Failed to create conversation';
      },
    )

    .addMatcher(
      (action): action is { type: string } =>
        action.type === 'chat/createConversationWithAttachments/pending',
      (state) => {
        state.isCreatingConversation = true;
        state.error = null;
      },
    )
    .addMatcher(
      (action): action is { type: string; payload: CreateConversationResponse } =>
        action.type === 'chat/createConversationWithAttachments/fulfilled',
      (state, action) => {
        state.isCreatingConversation = false;
        if (action.payload?.data) {
          const conversation = action.payload.data;

          const normalizedMessages =
            conversation.messages?.map((msg) => ({
              ...msg,
              id: msg.uuid || msg.id,
            })) || [];

          const normalizedParticipants =
            conversation.participants?.map((participant) => ({
              ...participant,
              id: participant.uuid || participant.id,
            })) || [];

          const convWithFlags = deriveConversationFlags(conversation);

          const normalizedConversation = {
            ...convWithFlags,
            id: convWithFlags.uuid || convWithFlags.id,
            messages: normalizedMessages,
            participants: normalizedParticipants,
          };

          state.currentConversation = normalizedConversation;
          state.conversations.push(normalizedConversation);

          const serviceRequestId =
            conversation.service_request_uuid || conversation.service_request_id;
          if (serviceRequestId) {
            if (!state.serviceRequestConversationsCache[serviceRequestId]) {
              state.serviceRequestConversationsCache[serviceRequestId] = {
                conversations: [],
                lastFetched: Date.now(),
              };
            }
            state.serviceRequestConversationsCache[serviceRequestId].conversations.push(
              normalizedConversation,
            );
            state.serviceRequestConversationsCache[serviceRequestId].lastFetched = Date.now();
          }

          const workOrderId = conversation.work_order_uuid || conversation.work_order_id;
          if (workOrderId) {
            if (!state.workOrderConversationsCache[workOrderId]) {
              state.workOrderConversationsCache[workOrderId] = {
                conversations: [],
                lastFetched: Date.now(),
              };
            }
            state.workOrderConversationsCache[workOrderId].conversations.push(
              normalizedConversation,
            );
            state.workOrderConversationsCache[workOrderId].lastFetched = Date.now();
          }
        }
      },
    )
    .addMatcher(
      (action): action is { type: string; error: { message?: string } } =>
        action.type === 'chat/createConversationWithAttachments/rejected',
      (state, action) => {
        state.isCreatingConversation = false;
        state.error = action.error.message || 'Failed to create conversation';
      },
    )
    // Mark Conversation As Read
    .addMatcher(
      (action): action is { type: string; meta: { arg: string } } =>
        action.type === 'chat/markConversationAsRead/fulfilled',
      (state, action) => {
        // The conversationId is passed as the first argument to the thunk
        const conversationId = action.meta?.arg;
        if (!conversationId) return;

        const readAt = new Date().toISOString();

        // Helper to mark conversation messages as read
        const markAsRead = (conv: any) => {
          if (!conv) return;
          if (conv.uuid !== conversationId && conv.id !== conversationId) return;

          conv.messages?.forEach((msg: any) => {
            msg.is_read = true;
            msg.read_at = readAt;
            msg.status = 'read';
            msg.checkmark_count = 2;
            msg.has_double_checkmark = true;
          });
          conv.unread_messages_count = 0;
        };

        // Update currentConversation
        markAsRead(state.currentConversation);

        // Update in conversations array
        state.conversations.forEach(markAsRead);

        // Update all caches
        Object.values(state.serviceRequestConversationsCache).forEach((cache) => {
          cache.conversations?.forEach(markAsRead);
        });
        Object.values(state.workOrderConversationsCache).forEach((cache) => {
          cache.conversations?.forEach(markAsRead);
        });
      },
    );
};

export default extraReducers;
